import {
  IonCardContent,
  IonCol,
  IonGrid,
  IonRow
} from "@ionic/react";
import React, { useEffect } from "react";

import AnnouncementBanner from "../components/AnnouncementBanner";
import Content from "../components/Content";
import PleaseLoginCard from "../components/PleaseLoginCard";
import RequestAccessCard from "../components/RequestAccessCard";
import SongList from "../components/SongList";
import { useConfig } from "../data/store/useConfig";
import { useProfile } from "../data/store/useProfile";
import { useSongs } from "../data/store/useSongs";
import { ENABLE_GROUPS } from "../config/env";
import isEmpty from "lodash.isempty";
import CompactSongList from "../components/CompactSongList";
import { CompactToggle } from "../components/CompactToggle";
import "../components/styles.scss";
import { usePageTitle } from "../util/sugar";


const UnfilteredSongsPage: React.FC = () => {
  const { authorId, status: userStatus,compact } = useProfile();
  const {
    fetchSome,
    observe,
    status,
    initialFetchStatus,
    timeline,
    needsFeedRefresh,
  } = useSongs();
  const { isAlumni, activeMemberships } = useConfig();



  const membership = activeMemberships();
  const unauthenticated = userStatus === "unauthenticated";
  const authenticated = userStatus === "authenticated";
  // When groups are disabled, always show status; otherwise only when user has membership
  const songStatus = !ENABLE_GROUPS || !isEmpty(membership) ? status : "idle";

  usePageTitle("Songs");


  const handleFetchMore = () => {
    fetchSome(membership, unauthenticated);
  };

  // Backs continuous (YouTube-style) autoplay for "Play All": load the next
  // page of the feed and report which song ids are new, so playback keeps going
  // past the visible page without pre-queuing the whole feed. Reads the store
  // directly so the before/after diff is accurate even mid-playback.
  const loadMoreFeed = async (): Promise<string[]> => {
    const before = new Set(useSongs.getState().timeline.map((r) => r.id));
    await fetchSome(membership, unauthenticated);
    return useSongs
      .getState()
      .timeline.map((r) => r.id)
      .filter((id) => !before.has(id));
  };

  useEffect(() => {
    const shouldFetch = ENABLE_GROUPS ? !isEmpty(membership) : true;
    // For SADFAM (no groups), fetch as soon as auth state is known — logged-in
    // or not. For groups builds, require authenticated + membership.
    const isReady = ENABLE_GROUPS ? authenticated : userStatus !== "unknown";

    // Refetch on first mount AND whenever the cached timeline belongs to a
    // different visibility scope (login/logout, group-page visit) — the
    // store resets the timeline + cursor for the new scope.
    if (isReady && shouldFetch && (initialFetchStatus === "initial" || needsFeedRefresh(membership))) {
      fetchSome(membership, unauthenticated);
    }
    // fetchSome only loads a snapshot at mount/scope-change time — without
    // this, newly-transmitted songs never appear until something else
    // remounts the page (e.g. a hard refresh). observe() attaches a live
    // Firestore listener that prepends new/changed songs to the timeline
    // as they arrive; group-scoped song pages already do this, the main
    // feed just never called it.
    if (isReady && shouldFetch) {
      observe(membership);
    }
  }, [authenticated, userStatus, unauthenticated, initialFetchStatus, membership, fetchSome, needsFeedRefresh, observe]);

  if (ENABLE_GROUPS && isAlumni() === false) {
    return (
      <Content>
        <RequestAccessCard authorId={authorId} />
      </Content>
    );
  } else if (ENABLE_GROUPS && unauthenticated) {
    return (
      <Content>
        <PleaseLoginCard action="View Songs" />
      </Content>
    );
  }

  return (
    <Content>
      <IonGrid style={{margin:0,padding:0}}>
        <IonRow>
          <IonCol style={{padding:0,margin:0}} sizeMd="8" sizeLg="6" offsetMd="2" offsetLg="3">
            {/* SON-26: the song feed is the default route's main content; give
                it a single semantic page heading (visually hidden to preserve
                the existing layout) so the view isn't "no heading structure". */}
            <h1 className="sr-only">Songs</h1>
            <AnnouncementBanner />
            <IonCardContent
              style={{
                padding: "0px",
                borderRadius: "8px",
                margin: "0 16px",
              }}
            >
            <CompactToggle edgeInset={0} />
            </IonCardContent>

            {ENABLE_GROUPS && isEmpty(membership) && (
              <RequestAccessCard message="You have no active classes" headingLevel={2} />
            )}

            {compact ? (
              <CompactSongList
                songRefs={timeline}
                fetchMore={handleFetchMore}
                status={songStatus}
                loadMore={loadMoreFeed}
              />
            ) : (
              <SongList
                status={songStatus}
                fetchMore={handleFetchMore}
                songRefs={timeline}
                loadMore={loadMoreFeed}
              />
            )}
          </IonCol>
        </IonRow>
      </IonGrid>
    </Content>
  );
};

export default UnfilteredSongsPage;
